import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FilterRail } from '@/components/job-feed/FilterRail';
import { JobRowComfortable } from '@/components/job-feed/JobRowComfortable';
import { JobRowCompact } from '@/components/job-feed/JobRowCompact';
import type { SortId } from '@/components/job-feed/jobFeed.types';
import { SortMenu } from '@/components/job-feed/SortMenu';
import { KeyboardShortcutKey } from '@/components/ui/KeyboardShortcutKey';
import { PeekingGoblin } from '@/components/ui/PeekingGoblin';
import { ScrollPane } from '@/components/ui/ScrollPane';
import { cx } from '@/cva.config';
import { useJobs } from '@/hooks/useJobs';
import { postedDaysAgo } from '@/lib/jobAdapter';
import {
  useFilterRail,
  useHasSelection,
  useJobFilters,
  useVisibleJobs,
} from '@/providers/dashboard/JobBrowserContext';
import { useDisplayDensity } from '@/providers/dashboard/ShellContext';

interface JobFeedProps {
  scanning?: boolean;
}

export const JobFeed = ({ scanning = false }: JobFeedProps) => {
  const jobs = useJobs();
  const { filters, setFilters } = useJobFilters();
  const hideActions = useHasSelection();
  const { density } = useDisplayDensity();
  const { railOpen, toggleRail } = useFilterRail();
  const { setVisibleJobIds } = useVisibleJobs();

  const [sort, setSort] = useState<SortId>('score');

  const filtered = useMemo(() => {
    let result = jobs.slice();

    const { status, sources, minScore, query } = filters;

    if (status !== 'all') {
      if (status === 'failed') {
        result = result.filter(
          ({ status: jobStatus }) =>
            jobStatus === 'score_failed' || jobStatus === 'tailor_failed',
        );
      } else {
        result = result.filter(({ status: jobStatus }) => jobStatus === status);
      }
    }

    if (sources.length > 0) {
      result = result.filter((job) => sources.includes(job.source));
    }

    if (minScore > 0) {
      result = result.filter((job) => (job.matchScore ?? 0) >= minScore);
    }

    if (query) {
      result = result.filter(
        ({ title, company }) =>
          title.toLowerCase().includes(query)
          || company.toLowerCase().includes(query),
      );
    }

    result.sort((a, b) => {
      if (sort === 'score') {
        return (b.matchScore ?? -1) - (a.matchScore ?? -1);
      }

      if (sort === 'recent') {
        return (
          (postedDaysAgo(a.postedAt) ?? 999)
          - (postedDaysAgo(b.postedAt) ?? 999)
        );
      }

      if (sort === 'salary') {
        return (b.salaryMax ?? 0) - (a.salaryMax ?? 0);
      }

      if (sort === 'company') {
        return a.company.localeCompare(b.company);
      }

      return 0;
    });

    return result;
  }, [jobs, filters, sort]);

  const counts = useMemo(() => {
    const aggregated: Record<string, number> = { all: jobs.length };

    for (const { status, source } of jobs) {
      aggregated[status] = (aggregated[status] ?? 0) + 1;
      aggregated[source] = (aggregated[source] ?? 0) + 1;
    }

    aggregated.failed =
      (aggregated.score_failed ?? 0) + (aggregated.tailor_failed ?? 0);

    return aggregated;
  }, [jobs]);

  const isCompact = density === 'compact';

  useEffect(() => {
    setVisibleJobIds(filtered.map((job) => job.id));
  }, [filtered, setVisibleJobIds]);

  return (
    <div className="flex min-h-0 flex-1">
      <FilterRail
        counts={counts}
        filters={filters}
        onToggle={toggleRail}
        open={railOpen}
        setFilters={setFilters}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-3 border-b border-border bg-surface/30 px-4 py-2">
          <div className="relative w-72">
            <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-subtle" />
            <input
              className="h-7 w-full rounded-md border border-border bg-surface/60 pr-12 pl-7 text-xs lg:text-sm text-foreground outline-none placeholder:text-subtle focus:border-accent"
              data-testid="job-search-input"
              name="job-search-input"
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  query: event.target.value,
                }))
              }
              placeholder="Filter jobs by title or company…"
              type="text"
              value={filters.query}
            />
            <div className="absolute top-1/2 right-2 -translate-y-1/2">
              <KeyboardShortcutKey>/</KeyboardShortcutKey>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <SortMenu onChange={setSort} value={sort} />
            <div className="text-xs lg:text-sm text-foreground-muted">
              <span className="font-mono text-foreground tabular-nums">
                {filtered.length}
              </span>
              <span> of </span>
              <span className="font-mono tabular-nums">{jobs.length}</span>
            </div>
          </div>
        </div>

        {scanning && (
          <div className="scan-sweep relative overflow-hidden border-b border-border bg-surface/40">
            <div className="flex items-center gap-3 px-4 py-2">
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-2 animate-ping rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-accent" />
              </span>
              <span className="font-mono text-xs lg:text-sm text-foreground-muted">
                Goblin scanning sources…
              </span>
              <span className="ml-auto font-mono text-2xs lg:text-xs text-subtle tabular-nums">
                scrape ▸ score ▸ tailor
              </span>
            </div>
          </div>
        )}

        <ScrollPane className="min-h-0 flex-1">
          {isCompact && filtered.length > 0 && (
            <div
              className={cx(
                'sticky top-0 z-10 grid items-center gap-3 border-b border-border bg-surface/95 px-4 py-1.5 font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase backdrop-blur-sm',
                hideActions
                  ? 'grid-cols-[96px_2.4fr_1fr_1fr_110px_230px]'
                  : 'grid-cols-[96px_2.4fr_1fr_1fr_110px_230px_70px]',
              )}
            >
              <div>Score</div>
              <div>Title / Company</div>
              <div>Location</div>
              <div>Salary</div>
              <div>Source</div>
              <div>Status</div>
              {!hideActions && <div>Actions</div>}
            </div>
          )}
          {!isCompact && filtered.length > 0 && (
            <div
              className={cx(
                'sticky top-0 z-10 grid grid-cols-[96px_minmax(0,1fr)_120px_120px_120px] items-center gap-x-4 border-b border-border bg-surface/95 px-4 py-1.5 font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase backdrop-blur-sm',
                hideActions && 'grid-cols-[96px_minmax(0,1fr)_220px_230px]',
              )}
            >
              <div>Score</div>
              <div>Listing</div>
              <div>Tech stack</div>
              <div>Status</div>
              {!hideActions && <div>Actions</div>}
            </div>
          )}
          {filtered.length === 0 ? (
            <PeekingGoblin
              caption={
                filters.query
                  ? `Nothing matched "${filters.query}". The goblin is napping.`
                  : 'No jobs in this view. The goblin is on the prowl…'
              }
            />
          ) : (
            filtered.map((job) =>
              isCompact ? (
                <JobRowCompact job={job} key={job.id} />
              ) : (
                <JobRowComfortable job={job} key={job.id} />
              ),
            )
          )}
        </ScrollPane>
      </div>
    </div>
  );
};
